import { useEffect, useState } from 'react';

const DESMOS_SRC =
  `https://www.desmos.com/api/v1.10/calculator.js?apiKey=${import.meta.env.VITE_DESMOS_API_KEY}`;

// The Desmos calculator bundle is large, so instead of loading it on every
// page via a <script> tag in index.html, we inject it on demand the first
// time a calculator is needed. The promise is memoised so the script is only
// ever requested once, regardless of how many consumers ask for it.
let desmosPromise: Promise<void> | null = null;

export const loadDesmos = () => {
  if (desmosPromise) return desmosPromise;

  desmosPromise = new Promise<void>((resolve, reject) => {
    if (typeof window !== 'undefined' && 'Desmos' in window) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = DESMOS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      desmosPromise = null; // allow a retry on next request
      reject(new Error('Failed to load the Desmos calculator'));
    };
    document.head.appendChild(script);
  });

  return desmosPromise;
};

/**
 * Loads the Desmos bundle on demand and reports when the global `Desmos`
 * object is available. Pass `enabled = false` to defer loading until it's
 * actually needed (e.g. until a dialog is first opened).
 */
export const useDesmos = (enabled = true) => {
  const [ready, setReady] = useState(() => typeof window !== 'undefined' && 'Desmos' in window);

  useEffect(() => {
    if (!enabled || ready) return;

    let active = true;
    loadDesmos()
      .then(() => { if (active) setReady(true); })
      .catch(() => { /* leave not-ready; a later attempt retries */ });

    return () => { active = false; };
  }, [enabled, ready]);

  return ready;
};
